import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, inject, Signal, computed, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { CabinetService } from '@cabinet/cabinet.service';
import { ProjectModel } from '@core/models/project.model';
import { StatusModel } from '@core/models/status.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import * as LayoutAction from '@core/redux/layout/layout.actions';
import * as PopupAction from '@core/redux/popup/popup.actions';
import * as ProjectAction from '@core/redux/project/project.actions';
import { DragDropService } from '@core/services/drag-drop.service';
import { TaskTransferService } from '@core/services/task-transfer.service';
import { select, Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';

import { ProjectFormComponent } from '../project-form/project-form.component';
import { ProjectMenuComponent } from '../project-menu/project-menu.component';

interface ProjectFormPopupState {
  isOpen: boolean;
  left: number;
  top: number;
}

interface ProjectMenuPopupState {
  isOpen: boolean;
  position: number;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.less'],
  standalone: true,
  imports: [CommonModule, RouterModule, ProjectFormComponent, ProjectMenuComponent, FormsModule, ReactiveFormsModule],
  providers: [DeviceDetectorService, CabinetService],
})
export class SidebarComponent {
  projectList = input<ProjectModel[]>([]);

  @Input() taskOpenId!: string;
  @Input() projectSelectedId!: string;

  private readonly deviceService = inject(DeviceDetectorService);
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly router = inject(Router);
  private readonly cabinetService = inject(CabinetService);
  private readonly destroyed = inject(DestroyRef);
  private readonly dragDropService = inject(DragDropService);
  private readonly taskTransferService = inject(TaskTransferService);

  // Ключ для localStorage
  private readonly EXPANDED_PROJECTS_KEY = 'sidebar_expanded_projects';

  readonly projectsTree: Signal<ProjectModel[]> = computed(() => this.buildProjectTree(this.projectList()));

  readonly isShowSidebarFavorite = toSignal(
    this.store.pipe(select(fromRoot.getShowSidebarFavorite), takeUntilDestroyed(this.destroyed)),
    { initialValue: false }
  );

  readonly isShowSidebarChat = toSignal(
    this.store.pipe(select(fromRoot.getShowSidebarChat), takeUntilDestroyed(this.destroyed)),
    { initialValue: false }
  );

  readonly projectSelectedIdUpdateItem = toSignal(
    this.store.pipe(select(fromRoot.getProjectSelectedIdForUpdate), takeUntilDestroyed(this.destroyed)),
    { initialValue: '' }
  );

  readonly notificationsTask = toSignal(
    this.store.pipe(select(fromRoot.getNotificationsTask), takeUntilDestroyed(this.destroyed)),
    { initialValue: [] as TaskModel[] }
  );

  readonly favoriteTask = toSignal(
    this.store.pipe(select(fromRoot.getFavoritesTask), takeUntilDestroyed(this.destroyed)),
    { initialValue: [] as TaskModel[] }
  );

  readonly popupProjectForm = toSignal(
    this.store.pipe(select(fromRoot.getPopupProjectForm), takeUntilDestroyed(this.destroyed)),
    {
      initialValue: { isOpen: false, top: 0, left: 0 } as ProjectFormPopupState,
    }
  );

  readonly popupProjectMenu = toSignal(
    this.store.pipe(select(fromRoot.getPopupProjectMenu), takeUntilDestroyed(this.destroyed)),
    { initialValue: { isOpen: false, position: 0 } as ProjectMenuPopupState }
  );

  readonly showStatusDialog = computed(() => this.taskTransferService.showStatusDialog());
  readonly currentTask = computed(() => this.taskTransferService.currentTask());
  readonly targetProjectId = computed(() => this.taskTransferService.targetProjectId());

  readonly statuses = toSignal(this.store.pipe(select(fromRoot.getStatusEntities)), {
    initialValue: [] as StatusModel[],
  });

  /**
   * Получает состояние развернутых проектов из localStorage
   */
  private getExpandedProjectsFromStorage(): { expandedProjects: Set<string>; isFirstTime: boolean } {
    try {
      const stored = localStorage.getItem(this.EXPANDED_PROJECTS_KEY);
      if (stored !== null) {
        const expandedIds = JSON.parse(stored);
        return {
          expandedProjects: new Set(expandedIds),
          isFirstTime: false,
        };
      }
    } catch (error) {
      console.warn('Ошибка при чтении состояния развернутых проектов из localStorage:', error);
    }
    return {
      expandedProjects: new Set(),
      isFirstTime: true,
    };
  }

  /**
   * Сохраняет состояние развернутых проектов в localStorage
   */
  private saveExpandedProjectsToStorage(expandedProjects: Set<string>): void {
    try {
      const expandedIds = Array.from(expandedProjects);
      localStorage.setItem(this.EXPANDED_PROJECTS_KEY, JSON.stringify(expandedIds));
    } catch (error) {
      console.warn('Ошибка при сохранении состояния развернутых проектов в localStorage:', error);
    }
  }

  /**
   * Собирает все развернутые проекты из дерева
   */
  private collectExpandedProjects(projects: ProjectModel[]): Set<string> {
    const expanded = new Set<string>();

    const traverse = (projectList: ProjectModel[]) => {
      projectList.forEach(project => {
        if (project.isExpanded && project.children && project.children.length > 0) {
          expanded.add(project.id);
        }
        if (project.children && project.children.length > 0) {
          traverse(project.children);
        }
      });
    };

    traverse(projects);
    return expanded;
  }

  /**
   * Creates a clone of the project with mutable properties
   */
  private cloneProject(project: ProjectModel): ProjectModel {
    const clone = new ProjectModel();

    // Copy all properties
    clone.id = project.id;
    clone.name = project.name;
    clone.color = project.color;
    clone.description = project.description;
    clone.isArchive = project.isArchive;
    clone.canUpdate = project.canUpdate;
    clone.parentId = project.parentId;
    clone.isExpanded = project.isExpanded || false;
    clone.level = project.level || 0;
    clone.children = [];

    return clone;
  }

  buildProjectTree(projects: ProjectModel[]): ProjectModel[] {
    if (!projects || !projects.length) {
      return [];
    }

    // Получаем сохраненное состояние развернутых проектов
    const { expandedProjects, isFirstTime } = this.getExpandedProjectsFromStorage();

    // Create copies of all projects
    const projectMap = new Map<string, ProjectModel>();
    projects.forEach(project => {
      projectMap.set(project.id, this.cloneProject(project));
    });

    // First find all root projects (no parentId)
    const rootProjects: ProjectModel[] = [];
    projectMap.forEach(project => {
      if (!project.parentId) {
        rootProjects.push(project);
      }
    });

    // For each root project, recursively add children
    rootProjects.forEach(project => {
      project.children = this.findChildren(projectMap, project.id, expandedProjects);
      // Корневые проекты: при первом запуске развернуты если есть дети, иначе восстанавливаем из localStorage
      if (project.children.length > 0) {
        project.isExpanded = isFirstTime ? true : expandedProjects.has(project.id);
      } else {
        project.isExpanded = false;
      }
      project.level = 0;
    });

    return rootProjects;
  }

  findChildren(
    projectMap: Map<string, ProjectModel>,
    parentId: string,
    expandedProjects: Set<string>,
    level: number = 1
  ): ProjectModel[] {
    const children: ProjectModel[] = [];

    projectMap.forEach(project => {
      if (project.parentId === parentId) {
        const childClone = this.cloneProject(project);
        childClone.children = this.findChildren(projectMap, project.id, expandedProjects, level + 1);
        // Восстанавливаем состояние из localStorage, по умолчанию свернуты
        if (childClone.children.length > 0) {
          childClone.isExpanded = expandedProjects.has(project.id);
        } else {
          childClone.isExpanded = false;
        }
        childClone.level = level;
        children.push(childClone);
      }
    });

    return children;
  }

  /**
   * Находит проект в дереве и раскрывает его
   */
  private expandProjectInTree(projectId: string): void {
    const currentTree = this.projectsTree();

    const findAndExpand = (projects: ProjectModel[]): boolean => {
      for (const project of projects) {
        if (project.id === projectId && project.children && project.children.length > 0) {
          project.isExpanded = true;
          return true;
        }
        if (project.children && project.children.length > 0) {
          if (findAndExpand(project.children)) {
            return true;
          }
        }
      }
      return false;
    };

    if (findAndExpand(currentTree)) {
      // Сохраняем новое состояние в localStorage
      const expandedProjects = this.collectExpandedProjects(currentTree);
      this.saveExpandedProjectsToStorage(expandedProjects);
    }
  }

  toggleProjectExpand(project: ProjectModel, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    project.isExpanded = !project.isExpanded;

    // Сохраняем новое состояние в localStorage
    const currentTree = this.projectsTree();
    const expandedProjects = this.collectExpandedProjects(currentTree);
    this.saveExpandedProjectsToStorage(expandedProjects);
  }

  getProjectIndentation(level: number): string {
    if (level === 0) {
      return '7px';
    } else if (level === 1) {
      return '15px';
    } else {
      return `${15 + level * 7}px`;
    }
  }

  onOpenProjectMenu($event: MouseEvent, projectId: string): void {
    $event.stopPropagation();
    $event.preventDefault();
    const bottom = window.innerHeight - $event.pageY - 67;
    const position = bottom < 15 ? 15 : bottom;
    this.store.dispatch(new ProjectAction.SelectAction(projectId));
    this.store.dispatch(new PopupAction.OpenProjectMenu(position));
  }

  onOpenCreateForm($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    let position = 0;
    const popupHeight = 295;
    const halfPopupHeight = popupHeight / 2;
    const screenHeight = window.innerHeight;
    const topHeight = $event.pageY;
    const bottomHeight = screenHeight - topHeight;

    if (halfPopupHeight <= topHeight && halfPopupHeight <= bottomHeight) {
      position = topHeight - halfPopupHeight;
    } else if (halfPopupHeight > topHeight) {
      position = 15;
    } else if (halfPopupHeight > bottomHeight) {
      position = screenHeight - popupHeight - 15;
    }
    this.store.dispatch(new PopupAction.OpenProjectFormMenu(position, 100));
  }

  onCloseSidebar(): void {
    this.store.dispatch(new LayoutAction.CloseSidebar());
  }

  onToggleFavorite(): void {
    this.store.dispatch(new LayoutAction.ToggleSidebarFavorite());
  }

  onToggleChat(): void {
    this.store.dispatch(new LayoutAction.ToggleSidebarChat());
  }

  onOpenTask(task: TaskModel): void {
    if (this.cabinetService.layout === 'board') {
      this.router.navigate(['/cabinet', task.projectId, 'load', { outlets: { task: ['view', task.id] } }], {
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate(['/cabinet', task.projectId, 'load', 'list'], {
        queryParams: { id: task.id },
        queryParamsHandling: 'merge',
      });
    }
    if (this.deviceService.isMobile()) {
      this.onCloseSidebar();
    }
  }

  onChangeProject(id: string): void {
    // Раскрываем проект если у него есть дочерние элементы
    this.expandProjectInTree(id);

    if (this.cabinetService.layout === 'board') {
      this.router.navigate(['/cabinet', id, 'load']);
    } else {
      this.router.navigate(['/cabinet', id, 'load', 'list']);
    }
    if (this.deviceService.isMobile()) {
      this.onCloseSidebar();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    // Добавляем визуальную обратную связь
    (event.target as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    // Убираем визуальную обратную связь
    (event.target as HTMLElement).classList.remove('drag-over');
  }

  onDropOnAllTasks(event: DragEvent): void {
    event.preventDefault();
    (event.target as HTMLElement).classList.remove('drag-over');

    // Для "Все задачи" не меняем проект
  }

  onDropOnProject(event: DragEvent, projectId: string): void {
    event.preventDefault();
    (event.target as HTMLElement).classList.remove('drag-over');

    const draggedTask = this.dragDropService.getDraggedTask();
    if (!draggedTask || !draggedTask.id) {
      return;
    }

    this.taskTransferService.handleTaskTransfer(draggedTask, projectId);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
  }
}
